import React, { useState, useMemo } from "react";
import styles from "./BundlesHero.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import BestSellers from "components/Heros/BestSellers/BestSellers";
import { getGameList } from "constants/GameListAdapter";
import BundleGameCard from "components/Heros/BundleCard/BundleCard";
import { addMultipleToCart } from "actions/cart";
import GameInfoModal from "components/Modals/GameInfoModal/GameInfoModal";
import BundlesUnderline from "images/homepage/BundlesUnderline.png";
import {
    TOP_SELLERS_ENABLED,
    TOP_SELLERS_COUNT,
    AUTO_DISCOUNT_PERCENT,
    AUTO_DISCOUNT_MIN_GAMES,
} from "constants/DiscountConfig";

const TABS = [
    { key: "gameShows", label: "Game Shows", filter: g => g.gameShow },
    { key: "partyGames", label: "Party Games", filter: g => g.partyGame },
];

const BundlesHero = () => {
    const [modalGame, setModalGame] = useState(null);
    const [activeTab, setActiveTab] = useState("gameShows");

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Get top seller games by tab (show regardless of ownership)
    const topSellerGames = useMemo(() => {
        const tab = TABS.find(t => t.key === activeTab);
        return getGameList()
            .filter(g => g.status === "enabled" && g.tags?.includes("best-seller") && tab.filter(g))
            .slice(0, TOP_SELLERS_COUNT);
    }, [activeTab]);

    const handleAddToCart = (games) => {
        if (games.length) {
            dispatch(addMultipleToCart(games));
        }
    };

    if (!TOP_SELLERS_ENABLED) return <BestSellers />;

    // If not enough top sellers, show BestSellers fallback
    if (topSellerGames.length < 2) return <BestSellers />;

    return (
        <div className={styles.bundlesContainer}>
            {/* Header */}
            <div className={styles.headerArea}>
                <div className={styles.sectionLabel}>
                    <span className={styles.labelText}>TOP SELLERS</span>
                    <img src={BundlesUnderline} alt="" className={styles.labelUnderline} />
                </div>
                <div className={styles.title}>Grab all our top sellers get {AUTO_DISCOUNT_PERCENT}% off</div>
                <p className={styles.subtitle}>
                    Buy {AUTO_DISCOUNT_MIN_GAMES} or more games and save automatically
                </p>
            </div>

            {/* Tabs */}
            <div className={styles.tabBar}>
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Game cards */}
            <div className={styles.bundleContent}>
                {/* Desktop: game cards in a row */}
                <div className={styles.gameRow}>
                    {topSellerGames.map(game => (
                        <div key={game.gameId} className={styles.gameSlot}>
                            <BundleGameCard
                                game={game}
                                noButton={true}
                                onClick={() => setModalGame(game)}
                                selected={false}
                                isActive={false}
                                onLearnMore={(e) => { e.stopPropagation(); setModalGame(game); }}
                                showInfoButton={true}
                                compact={true}
                            />
                        </div>
                    ))}
                </div>

                {/* Mobile: compact 2-col grid */}
                <div className={styles.mobileGameGrid}>
                    {topSellerGames.map(game => (
                        <div key={game.gameId} className={styles.mobileGameSlot}>
                            <BundleGameCard
                                game={game}
                                noButton={true}
                                onClick={() => setModalGame(game)}
                                selected={false}
                                isActive={false}
                                onLearnMore={(e) => { e.stopPropagation(); setModalGame(game); }}
                                showInfoButton={true}
                                compact={true}
                            />
                        </div>
                    ))}
                </div>

                {/* CTA Buttons */}
                <div className={styles.bundleCta}>
                    <button
                        className={styles.addButton}
                        onClick={() => handleAddToCart(topSellerGames)}
                    >
                        ADD TO BASKET
                    </button>

                    <button type="button" className={styles.shopAllButton} onClick={() => navigate("/shop")}>
                        Shop all games
                    </button>
                </div>
            </div>

            {/* Game Info Modal */}
            <GameInfoModal
                show={!!modalGame}
                game={modalGame}
                onClose={() => setModalGame(null)}
            />
        </div>
    );
};

export default BundlesHero;
